import React from 'react';
import {
    Notifications,
    Close,
    CheckCircle,
    Warning,
    Info,
    Error,
    Delete
} from '@mui/icons-material';
import { formatNotificationTitle, formatNotificationMessage } from '../../utils/notificationFormatter';

const NotificationPanel = ({ notifications, onMarkAsRead, onClose }) => {
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'success':
                return <CheckCircle className="text-green-500 text-xl" />;
            case 'warning':
                return <Warning className="text-amber-500 text-xl" />;
            case 'error':
                return <Error className="text-red-500 text-xl" />;
            default:
                return <Info className="text-blue-500 text-xl" />;
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'success':
                return 'border-l-green-500 bg-green-50';
            case 'warning':
                return 'border-l-amber-500 bg-amber-50';
            case 'error':
                return 'border-l-red-500 bg-red-50';
            default:
                return 'border-l-blue-500 bg-blue-50';
        }
    };

    const formatTime = (date) => {
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        if (minutes < 1) return 'À l\'instant';
        if (minutes < 60) return `Il y a ${minutes} min`;
        if (hours < 24) return `Il y a ${hours} h`;
        return `Il y a ${days} j`;
    };

    return (
        <div className="fixed right-6 top-20 w-96 bg-white rounded-3xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-hidden">
            {/* En-tête */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                    <div className="p-2 bg-amber-100 rounded-xl">
                        <Notifications className="text-amber-600 text-xl" />
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Notifications</h3>
                        <p className="text-sm text-gray-600">
                            {notifications.filter(n => !n.read).length} non lues
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <Close />
                </button>
            </div>

            {/* Liste des Notifications */}
            <div className="overflow-y-auto max-h-80">
                {notifications.length > 0 ? (
                    <div className="p-2">
                        {notifications.map((notification) => (
                            <div
                                key={notification.id}
                                className={`p-4 rounded-2xl border-l-4 mb-2 transition-all duration-300 ${
                                    notification.read
                                        ? 'bg-gray-50 border-l-gray-300'
                                        : getNotificationColor(notification.type)
                                }`}
                            >
                                <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                        {getNotificationIcon(notification.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h4 className={`font-semibold ${
                                                    notification.read ? 'text-gray-700' : 'text-gray-900'
                                                }`}>
                                                    {formatNotificationTitle(notification)}
                                                </h4>
                                                <p className={`text-sm mt-1 ${
                                                    notification.read ? 'text-gray-500' : 'text-gray-600'
                                                }`}>
                                                    {formatNotificationMessage(notification) || 'Nouvelle activité.'}
                                                </p>
                                            </div>
                                            {!notification.read && (
                                                <button
                                                    onClick={() => onMarkAsRead(notification.id)}
                                                    className="flex-shrink-0 p-1 text-gray-400 hover:text-amber-600 transition-colors"
                                                    title="Marquer comme lu"
                                                >
                                                    <CheckCircle className="text-sm" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between mt-2">
                                            <span className="text-xs text-gray-400">
                                                {formatTime(notification.time)}
                                            </span>
                                            {notification.read && (
                                                <span className="text-xs text-gray-400">Lu</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="text-6xl text-gray-300 mb-4">🔔</div>
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">
                            Aucune notification
                        </h3>
                        <p className="text-gray-400">
                            Vous êtes à jour !
                        </p>
                    </div>
                )}
            </div>

            {/* Pied de page */}
            {notifications.length > 0 && (
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <button className="w-full flex items-center justify-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors py-2">
                        <Delete className="text-sm" />
                        <span className="text-sm font-medium">Tout marquer comme lu</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationPanel;